import React, { useEffect, useState } from 'react';
import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Grid,
  GridItem,
  Input,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';
import { PROVINCES } from 'variables/options';
import { useAppSelector, useAppDispatch } from 'hooks';
import {
  setIncludeBilling,
  setPaymentMethod,
  setPaymentSchedule,
  setPaymentType,
  setBillingProvince,
  setBusinessCode,
  setCompanyFees,
  setBrokerFees,
  setTaxes,
  setFinancingType,
  setSignedContractAttached,
  setDownPayment,
  setDownPaymentAmount,
  setDownPaymentType,
} from 'reducers/commercialFormReducer';

// Agency Bill ONLY markets — Direct Bill radio disabled for these
const AGENCY_BILLING_MARKETS = [
  'Agile UW',
  'Anderson McTague',
  'Angus Miller',
  'ARAG Legal Solutions',
  'April Canada',
  'April Marine',
  'Beacon',
  'Brownstone Insurance Managers',
  'Burns & Wilcox',
  'BIG Lloyds',
  'Cansure',
  'CannGen',
  'CHES Special Risk',
  'Chutter',
  'CFC Underwriting',
  'Coast Underwriters',
  'Excess Underwriting',
  'Forward Insurance',
  'GroupOne',
  'I3 Underwriting',
  'Intact Public Entities',
  'K&K Canada',
  'Markel',
  'MedThree',
  'Oasis Outdoor Adventure',
  'Orbit',
  'Aviva Elite Special Events',
  'PAL',
  'Premier',
  'Revau',
  'Signature Risk',
  'Special Risk Insurance Managers',
  'SRIM',
  'Strategic Underwriting Managers',
  'SUM',
  'SWG Specialty',
  'AM Fredericks',
  'Southwestern Group',
  'SWG',
  'Totten Group',
  'Trinity Underwriting',
  'Unique Risks',
  'Victor',
  'Rise Underwriting Solutions',
];

// Direct Bill ONLY markets — Agency Bill radio disabled for these
const DIRECT_BILL_ONLY_MARKETS = [
  'Apollo',
];

interface CommercialBillingProps {
  isLocked?: boolean;
}

const CommercialBilling = ({
  isLocked = false,
}: CommercialBillingProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const billing = useAppSelector((state) => state.commercialForm.data.billing);
  const insuranceCompany = useAppSelector(
    (state) => state.commercialForm.data.insurance_company,
  );
  const premium = useAppSelector((state) => state.commercialForm.data.premium);
  const validationErrors = useAppSelector(
    (state) => state.commercialForm.validationErrors,
  );

  // Check if selected carrier requires agency billing only
  const requiresAgencyBilling = React.useMemo(() => {
    if (!insuranceCompany) return false;
    return AGENCY_BILLING_MARKETS.some((market) =>
      insuranceCompany.toLowerCase().includes(market.toLowerCase()),
    );
  }, [insuranceCompany]);

  // Check if selected carrier requires direct billing only
  const requiresDirectBilling = React.useMemo(() => {
    if (!insuranceCompany) return false;
    return DIRECT_BILL_ONLY_MARKETS.some((market) =>
      insuranceCompany.toLowerCase().includes(market.toLowerCase()),
    );
  }, [insuranceCompany]);

  // Local state for decimal inputs
  const [companyFeesInput, setCompanyFeesInput] = useState<string>('');
  const [brokerFeesInput, setBrokerFeesInput] = useState<string>('');
  const [taxesInput, setTaxesInput] = useState<string>('');
  const [downPaymentInput, setDownPaymentInput] = useState<string>('');

  // Sync local state with Redux state
  useEffect(() => {
    setCompanyFeesInput(
      billing.company_fees ? billing.company_fees.toString() : '',
    );
  }, [billing.company_fees]);

  useEffect(() => {
    setBrokerFeesInput(
      billing.broker_fees ? billing.broker_fees.toString() : '',
    );
  }, [billing.broker_fees]);

  useEffect(() => {
    setTaxesInput(billing.taxes ? billing.taxes.toString() : '');
  }, [billing.taxes]);

  useEffect(() => {
    const downPaymentAmount = (billing as any).down_payment_amount;
    setDownPaymentInput(downPaymentAmount ? downPaymentAmount.toString() : '');
  }, [(billing as any).down_payment_amount]);

  // Always set include_billing to true
  useEffect(() => {
    if (!billing.include_billing) {
      dispatch(setIncludeBilling(true));
    }
  }, [billing.include_billing, dispatch]);

  // Auto-select agency billing for agency-only markets
  useEffect(() => {
    if (requiresAgencyBilling && !requiresDirectBilling && billing.payment_method !== 'agency_bill') {
      dispatch(setPaymentMethod('agency_bill'));
    }
  }, [requiresAgencyBilling, requiresDirectBilling, billing.payment_method, dispatch]);

  // Auto-select direct billing for direct-only markets (Apollo)
  useEffect(() => {
    if (requiresDirectBilling && !requiresAgencyBilling && billing.payment_method === 'agency_bill') {
      dispatch(setPaymentMethod('eft_credit_card'));
    }
  }, [requiresDirectBilling, requiresAgencyBilling, billing.payment_method, dispatch]);

  // Set default financing_type to 'paid_full' for agency billing if it's empty
  useEffect(() => {
    if (billing.payment_method === 'agency_bill' && !billing.financing_type) {
      dispatch(setFinancingType('paid_full'));
    }
  }, [billing.payment_method, billing.financing_type, dispatch]);

  // Calculate total with proper decimal precision
  const total = React.useMemo(() => {
    const sum =
      premium +
      (billing.company_fees || 0) +
      (billing.broker_fees || 0) +
      (billing.taxes || 0);
    return Math.round(sum * 100) / 100;
  }, [premium, billing.company_fees, billing.broker_fees, billing.taxes]);

  const handleBillingTypeChange = (value: string) => {
    // Map billing type to payment_method
    const paymentMethod =
      value === 'agency_bill' ? 'agency_bill' : 'eft_credit_card';
    dispatch(setPaymentMethod(paymentMethod));
  };

  const handleFinancingChange = (value: 'paid_full' | 'premium_financing') => {
    dispatch(setFinancingType(value));
  };

  const handleSignedContractChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setSignedContractAttached(event.target.checked));
  };

  const handleDownPaymentChange = (value: 'yes' | 'no') => {
    dispatch(setDownPayment(value));
  };

  const handlePaymentScheduleChange = (
    value: 'monthly' | 'annual' | 'other',
  ) => {
    dispatch(setPaymentSchedule(value));
  };

  const handlePaymentTypeChange = (value: 'credit_card' | 'eft') => {
    dispatch(setPaymentType(value));
  };

  const handleDownPaymentAmountChange = (valueString: string) => {
    setDownPaymentInput(valueString);
    if (valueString === '') {
      dispatch(setDownPaymentAmount(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setDownPaymentAmount(value));
    }
  };

  const handleDownPaymentTypeChange = (value: string | number | null) => {
    dispatch(setDownPaymentType((value as string) || ''));
  };

  const handleProvinceChange = (value: string | number | null) => {
    dispatch(setBillingProvince((value as string) || ''));
  };

  const handleBusinessCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setBusinessCode(event.target.value));
  };

  const handleCompanyFeesChange = (valueString: string) => {
    setCompanyFeesInput(valueString);
    if (valueString === '') {
      dispatch(setCompanyFees(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setCompanyFees(value));
    }
  };

  const handleBrokerFeesChange = (valueString: string) => {
    setBrokerFeesInput(valueString);
    if (valueString === '') {
      dispatch(setBrokerFees(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setBrokerFees(value));
    }
  };

  const handleTaxesChange = (valueString: string) => {
    setTaxesInput(valueString);
    if (valueString === '') {
      dispatch(setTaxes(0));
      return;
    }
    const value = parseFloat(valueString);
    if (!isNaN(value)) {
      dispatch(setTaxes(value));
    }
  };

  // Map payment_method to billing type for UI
  const isDirectBill = billing.payment_method !== 'agency_bill';
  const isAgencyBill = billing.payment_method === 'agency_bill';

  // Ensure billingType is always a valid string for RadioGroup
  const radioValue: 'direct_bill' | 'agency_bill' = isAgencyBill
    ? 'agency_bill'
    : 'direct_bill';

  // Type assertion to handle TypeScript complexity warnings with conditional rendering
  return (
    <Card mb="20px" id="sectionCommercialBilling">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Billing
      </Text>

      <VStack spacing={4} align="stretch">
        <VStack spacing={4} align="stretch">
          {/* Billing Type */}
          <FormControl
            isRequired
            isInvalid={!!validationErrors?.billing?.payment_method}
          >
            <FormLabel ml="0">Billing Type</FormLabel>
            <RadioGroup
              value={radioValue}
              onChange={handleBillingTypeChange}
              isDisabled={isLocked}
            >
              <HStack spacing={8}>
                <Radio
                  value="direct_bill"
                  isDisabled={isLocked || (requiresAgencyBilling && !requiresDirectBilling)}
                >
                  Direct Bill Invoice
                </Radio>
                <Radio
                  value="agency_bill"
                  isDisabled={isLocked || (requiresDirectBilling && !requiresAgencyBilling)}
                >
                  Agency Invoice
                </Radio>
              </HStack>
            </RadioGroup>
          </FormControl>

          {/* Payment Plan and Payment Type - only shown for Direct Bill */}
          {isDirectBill && (
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel ml="0">Payment Plan Requested</FormLabel>
                  <RadioGroup
                    value={billing.payment_schedule || ''}
                    onChange={handlePaymentScheduleChange}
                    isDisabled={isLocked}
                  >
                    <HStack spacing={8}>
                      <Radio value="monthly" isDisabled={isLocked}>
                        Monthly
                      </Radio>
                      <Radio value="annual" isDisabled={isLocked}>
                        Annual
                      </Radio>
                      <Radio value="other" isDisabled={isLocked}>
                        Other
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl isRequired>
                  <FormLabel ml="0">Payment Type</FormLabel>
                  <RadioGroup
                    value={(billing as any).payment_type || ''}
                    onChange={handlePaymentTypeChange}
                    isDisabled={isLocked}
                  >
                    <HStack spacing={8}>
                      <Radio value="credit_card" isDisabled={isLocked}>
                        Credit Card
                      </Radio>
                      <Radio value="eft" isDisabled={isLocked}>
                        EFT
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
              </GridItem>
            </Grid>
          )}

          {/* Total Premium Excluding Tax - only shown for Direct Bill */}
          {isDirectBill && (
            <FormControl>
              <FormLabel ml="0">Total Premium Excluding Tax</FormLabel>
              <NumberInput value={premium} isReadOnly min={0} precision={2}>
                <NumberInputField placeholder="0.00" bg="gray.50" />
              </NumberInput>
            </FormControl>
          )}

          {isDirectBill && (
            <FormControl
              isRequired
              isInvalid={!!validationErrors?.billing?.province}
            >
              <FormLabel ml="0">Province</FormLabel>
              <SelectSearch
                options={PROVINCES}
                value={billing.province}
                onChange={handleProvinceChange}
                placeholder="Select province"
                allowClear={true}
                isDisabled={isLocked}
              />
            </FormControl>
          )}

          {/* Financing - only shown for Agency Invoice */}
          {isAgencyBill && (
            <>
              <FormControl isRequired>
                <FormLabel ml="0">Financing</FormLabel>
                <RadioGroup
                  value={billing.financing_type || 'paid_full'}
                  onChange={handleFinancingChange}
                  isDisabled={isLocked}
                >
                  <HStack spacing={8}>
                    <Radio value="paid_full" isDisabled={isLocked}>
                      Paid Full
                    </Radio>
                    <Radio value="premium_financing" isDisabled={isLocked}>
                      Premium Financing
                    </Radio>
                  </HStack>
                </RadioGroup>
                {billing.financing_type === 'premium_financing' && (
                  <FormControl isRequired mt={2}>
                    <Checkbox
                      isChecked={billing.signed_contract_attached || false}
                      onChange={handleSignedContractChange}
                      isDisabled={isLocked}
                    >
                      Signed contract attached in Power Broker
                    </Checkbox>
                  </FormControl>
                )}
              </FormControl>

              {/* Billing Details Grid - only shown for Agency Invoice */}
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                <GridItem>
                  <FormControl
                    isRequired
                    isInvalid={!!validationErrors?.billing?.province}
                  >
                    <FormLabel ml="0">Province</FormLabel>
                    <SelectSearch
                      options={PROVINCES}
                      value={billing.province}
                      onChange={handleProvinceChange}
                      placeholder="Select province"
                      allowClear={true}
                      isDisabled={isLocked}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isRequired
                    isInvalid={!!validationErrors?.billing?.business_code}
                  >
                    <FormLabel ml="0">Business Code</FormLabel>
                    <Input
                      variant="main"
                      value={billing.business_code}
                      onChange={handleBusinessCodeChange}
                      placeholder="Enter business code"
                      isDisabled={isLocked}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel ml="0">Total Premium</FormLabel>
                    <NumberInput
                      value={premium}
                      isReadOnly
                      min={0}
                      precision={2}
                    >
                      <NumberInputField placeholder="0.00" bg="gray.50" />
                    </NumberInput>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel ml="0">Company Fees</FormLabel>
                    <NumberInput
                      value={companyFeesInput}
                      onChange={handleCompanyFeesChange}
                      min={0}
                      step={0.01}
                      precision={2}
                      isDisabled={isLocked}
                    >
                      <NumberInputField placeholder="0.00" />
                    </NumberInput>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel ml="0">Broker Fees</FormLabel>
                    <NumberInput
                      value={brokerFeesInput}
                      onChange={handleBrokerFeesChange}
                      min={0}
                      step={0.01}
                      precision={2}
                      isDisabled={isLocked}
                    >
                      <NumberInputField placeholder="0.00" />
                    </NumberInput>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel ml="0">Taxes</FormLabel>
                    <NumberInput
                      value={taxesInput}
                      onChange={handleTaxesChange}
                      min={0}
                      step={0.01}
                      precision={2}
                      isDisabled={isLocked}
                    >
                      <NumberInputField placeholder="0.00" />
                    </NumberInput>
                  </FormControl>
                </GridItem>

                <GridItem colSpan={3}>
                  <FormControl>
                    <FormLabel ml="0" fontWeight="bold">
                      Total Invoiced To Customer (Sum)
                    </FormLabel>
                    <NumberInput value={total} isReadOnly min={0} precision={2}>
                      <NumberInputField
                        placeholder="0.00"
                        bg="gray.50"
                        fontWeight="bold"
                      />
                    </NumberInput>
                  </FormControl>
                </GridItem>
              </Grid>

              {/* Down Payment - shown for Agency Invoice */}
              <FormControl>
                <FormLabel ml="0">Down Payment</FormLabel>
                <RadioGroup
                  value={billing.down_payment || 'no'}
                  onChange={handleDownPaymentChange}
                  isDisabled={isLocked}
                >
                  <HStack spacing={8}>
                    <Radio value="yes" isDisabled={isLocked}>
                      Yes
                    </Radio>
                    <Radio value="no" isDisabled={isLocked}>
                      No
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>

              {/* Down Payment Details - only shown when Down Payment is Yes */}
              {billing.down_payment === 'yes' && (
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel ml="0">Down Payment Amount</FormLabel>
                      <NumberInput
                        value={downPaymentInput}
                        onChange={handleDownPaymentAmountChange}
                        min={0}
                        step={0.01}
                        precision={2}
                        isDisabled={isLocked}
                      >
                        <NumberInputField placeholder="0.00" />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel ml="0">Down Payment Type</FormLabel>
                      <SelectSearch
                        options={[
                          { value: 'e_transfer', label: 'E-Transfer' },
                          { value: 'cash', label: 'Cash' },
                          { value: 'cheque', label: 'Cheque' },
                          { value: 'other', label: 'Other' },
                        ]}
                        value={(billing as any).down_payment_type || ''}
                        onChange={handleDownPaymentTypeChange}
                        placeholder="Select type"
                        allowClear={true}
                        isDisabled={isLocked}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              )}
            </>
          )}

          {/* Down Payment - only shown for Direct Bill */}
          {isDirectBill && (
            <>
              <FormControl>
                <FormLabel ml="0">Down Payment</FormLabel>
                <RadioGroup
                  value={billing.down_payment || 'no'}
                  onChange={handleDownPaymentChange}
                  isDisabled={isLocked}
                >
                  <HStack spacing={8}>
                    <Radio value="yes" isDisabled={isLocked}>
                      Yes
                    </Radio>
                    <Radio value="no" isDisabled={isLocked}>
                      No
                    </Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>

              {/* Down Payment Details - only shown when Down Payment is Yes */}
              {billing.down_payment === 'yes' && (
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel ml="0">Down Payment Amount</FormLabel>
                      <NumberInput
                        value={downPaymentInput}
                        onChange={handleDownPaymentAmountChange}
                        min={0}
                        step={0.01}
                        precision={2}
                        isDisabled={isLocked}
                      >
                        <NumberInputField placeholder="0.00" />
                      </NumberInput>
                    </FormControl>
                  </GridItem>

                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel ml="0">Down Payment Type</FormLabel>
                      <SelectSearch
                        options={[
                          { value: 'e_transfer', label: 'E-Transfer' },
                          { value: 'cash', label: 'Cash' },
                          { value: 'cheque', label: 'Cheque' },
                          { value: 'other', label: 'Other' },
                        ]}
                        value={(billing as any).down_payment_type || ''}
                        onChange={handleDownPaymentTypeChange}
                        placeholder="Select type"
                        allowClear={true}
                        isDisabled={isLocked}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
              )}
            </>
          )}
        </VStack>
      </VStack>
    </Card>
  );
};

export default CommercialBilling;
